import React from "react";
import ReactDOM from "react-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LoadSpinner from "../../../utilities/LoadSpinner";
import AddFormToSectionModal from "./AddFormToSectionModal";
import ReadOnlyField from "./FormFieldReadOnly";
import { FormFieldInput } from "./FormFieldInput";
import { visibleFields, buildFieldsById, resolvePiping, formatAnswer } from "./formLogic";
import { isDisplayOnlyType } from "./formFieldHelpers";
import FormInfoScreen from "./FormInfoScreen";
import FormFieldImage from "./FormFieldImage";
import "./styles.css";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_learning_form_completion,
  API_get_learning_form_detail,
  API_post_learning_form_completion,
  API_get_learning_form_submission_for_grading,
} from "../../../constants";
import FormGradeModal from "../training_overview/FormGradeModal";

// One-question-per-screen player for forms saved with presentation="slides".
// Lazy: it pulls in framer-motion and is only needed when a learner starts one.
const FormPlayerLazy = React.lazy(() =>
  import("../take_training_employee/form_player/FormPlayer")
);

export function Form(props) {
  const { block, editor, homeCompany, employeeID, sectionUUID, userType, mode } = props;

  // Prefer the drag-proof primitive props; fall back to the legacy object prop
  // (formSelected) for content saved before formUuid existed. BlockNote wipes
  // object props on drag/copy-paste but keeps strings.
  const formSelected = block?.props?.formSelected || {};
  const formUuid = block?.props?.formUuid || formSelected.uuid || null;
  const formName = block?.props?.formName || formSelected.name || "";

  const isEditMode = mode === "edit";
  const isPreview = mode === "preview";

  const [showPicker, setShowPicker] = React.useState(false);
  // Slide-mode player (portaled full-screen), for presentation="slides" forms.
  const [showPlayer, setShowPlayer] = React.useState(false);

  // Complete-mode state (the form detail + the learner's completion)
  const [form, setForm] = React.useState(null);
  const [completion, setCompletion] = React.useState(null);
  const [responses, setResponses] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Inline grading (manager reviewing a trainee): the grade package is fetched
  // from a manager-only endpoint (it includes the answer_key the learner loader
  // strips) and handed to the shared FormGradeModal.
  const [gradeData, setGradeData] = React.useState(null); // {record, submission}
  const [gradeLoading, setGradeLoading] = React.useState(false);

  const handleFormSelect = (selected) => {
    if (!selected || !editor || !block) return;
    editor.updateBlock(block, {
      ...block,
      props: {
        ...block.props,
        formSelected: { uuid: selected.uuid, name: selected.name || "" },
        // Drag-proof primitive mirror (object props are wiped on drag).
        formUuid: selected.uuid || "",
        formName: selected.name || "",
      },
    });
  };

  // Load form detail + completion in complete mode
  React.useEffect(() => {
    if (mode !== "complete" || isPreview || !formUuid) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const res = await getRightworkAPI(API_get_learning_form_completion, {
          company_id: homeCompany,
          learning_form_uuid: formUuid,
          section_uuid: sectionUUID,
          store_team_member_id: employeeID,
        });
        if (cancelled) return;
        const payload = res.data?.redux?.payload || {};
        setForm(payload.form || null);
        setCompletion(payload.form_completion || null);
        if (payload.form_completion?.responses) {
          setResponses(payload.form_completion.responses);
        }
      } catch (error) {
        // Non-fatal: render a configuration notice
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isPreview, formUuid, sectionUUID, employeeID, homeCompany]);

  // Load the form detail (fields) in edit / preview mode so the section editor
  // and the track/module previews render the actual read-only form — the same
  // thing the employee sees — instead of just a reference card.
  React.useEffect(() => {
    // Complete mode is handled by the other effect — don't touch form here.
    if (!(isEditMode || isPreview)) return;
    if (!formUuid) {
      setForm(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      // Clear any previously loaded form first so a failed fetch (e.g. the
      // selected form was deleted, or the request is rejected) falls back to
      // the unavailable state instead of leaving the prior form's details for
      // a now-different UUID.
      setForm(null);
      try {
        const res = await getRightworkAPI(API_get_learning_form_detail, {
          company_id: homeCompany,
          learning_form_uuid: formUuid,
        });
        if (!cancelled) {
          setForm(res.data?.redux?.payload?.learning_form || null);
        }
      } catch (error) {
        // Non-fatal: fall back to the unavailable notice (form is already null).
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isPreview, formUuid, homeCompany]);

  // A submission to a manually-graded form that a manager hasn't scored yet.
  // This is NOT a failure and NOT "completed with a score" — it's awaiting
  // review, and must be shown distinctly (otherwise a gated pending submission
  // would read as "Not passed", telling the learner they failed before anyone
  // graded them).
  const isPendingGrade =
    Boolean(completion) && completion.grading_status === "pending";

  // On a gate_on_pass form, only an explicit pass (is_passed === true) satisfies
  // the section. A failing submission (false) keeps the card editable for a
  // retry. A submission still awaiting grading (pending) is handled separately
  // above and is not treated as a failure.
  const isBlockingFailure =
    Boolean(completion) &&
    Boolean(form?.gate_on_pass) &&
    completion.is_passed !== true &&
    !isPendingGrade;
  const isCompleted =
    Boolean(completion) && !isBlockingFailure && !isPendingGrade;
  const isManager = userType !== "Employee";
  // A "manager reviewing a learner" session is a manager (isManager) acting on
  // an explicit subject (employeeID). Self sessions — a learner, OR a manager
  // taking their own assigned training — carry no employeeID.
  const isManagerReviewingSubject = isManager && Boolean(employeeID);
  const canFill =
    form?.completed_by_role === "manager"
      ? // Manager forms post against the subject (the endpoint requires
        // store_team_member_id), so they're only fillable when a manager is
        // reviewing an explicit subject. In a self session there is no subject,
        // so fall through to the waiting state rather than an unsaveable form.
        isManagerReviewingSubject
      : // Employee forms are self-only: a manager reviewing a learner must not
        // fill (and the backend would reject) on the learner's behalf.
        !isManagerReviewingSubject;

  // A manager reviewing a learner can grade inline when this learner's
  // submission needs (or has) a manual grade — same action as Records, in context.
  const canManagerGrade =
    isManagerReviewingSubject &&
    Boolean(completion) &&
    (completion.grading_status === "pending" ||
      completion.grading_status === "graded");

  const openGrade = async () => {
    if (gradeLoading || !formUuid) return;
    setGradeLoading(true);
    try {
      const res = await getRightworkAPI(
        API_get_learning_form_submission_for_grading,
        {
          company_id: homeCompany,
          learning_form_uuid: formUuid,
          section_uuid: sectionUUID,
          store_team_member_id: employeeID,
        }
      );
      const payload = res.data?.redux?.payload;
      if (payload?.submission) {
        setGradeData({ record: payload.record, submission: payload.submission });
      }
    } catch (error) {
      // surfaced by the request layer
    } finally {
      setGradeLoading(false);
    }
  };

  const handleGradedInline = (_submission, gradedCompletion) => {
    // Reflect the new grade on the card immediately, and refresh the section so
    // gating/progress recompute.
    if (gradedCompletion) {
      setCompletion((prev) => ({ ...(prev || {}), ...gradedCompletion }));
    }
    setGradeData(null);
    // Match the section-refresh contract used by the other blocks (CompleteTrack
    // refetches only when this is truthy, then resets it) — a plain `true` is
    // race-safe, a toggle isn't.
    if (props.setRefreshSection) props.setRefreshSection(true);
  };

  const setResponse = (fieldId, value) =>
    setResponses((prev) => ({ ...prev, [fieldId]: value }));

  // Display conditions: only fields whose show_if currently holds are shown,
  // required, and submitted (the backend applies the same rules).
  const shownFields = visibleFields(form?.fields || [], responses);
  const fieldsById = buildFieldsById(form?.fields || []);
  const pipe = (text) => resolvePiping(text, responses, fieldsById);

  // Headline for the "Start" card in slide-show mode: the form's opening info
  // screen, or a legacy welcome screen on a form saved before those existed.
  const openingScreenTitle =
    ((form?.fields || [])[0]?.type === "info_screen" &&
      ((form?.fields || [])[0].label || "").trim()) ||
    form?.welcome_screen?.title ||
    "";

  const requiredUnfilled = shownFields.some((f) => {
    if (isDisplayOnlyType(f.type)) return false; // display-only, never "unfilled"
    if (!f.required) return false;
    const v = responses[f.id];
    // A required checkbox is an attestation — it must be checked.
    if (f.type === "checkbox") return !v;
    if (f.type === "yes_no") return !(v === true || v === false);
    if (Array.isArray(v)) return v.length === 0;
    return v === undefined || v === null || v === "";
  });

  const handleSubmit = async () => {
    if (!formUuid || isSubmitting || requiredUnfilled) return;
    setIsSubmitting(true);
    try {
      // Only ship answers for fields the learner actually saw.
      const shownIds = new Set(shownFields.map((f) => f.id));
      const payload_responses = {};
      Object.keys(responses).forEach((k) => {
        if (shownIds.has(k)) payload_responses[k] = responses[k];
      });
      const res = await postRightworkAPI(API_post_learning_form_completion, {
        company_id: homeCompany,
        learning_form_uuid: formUuid,
        section_uuid: sectionUUID,
        responses: JSON.stringify(payload_responses),
        store_team_member_id: employeeID,
      });
      const payload = res.data?.redux?.payload;
      // The backend always returns form_completion (with grading_status); the
      // fallback only guards a malformed 200. Derive grading_status so a
      // manually-graded form never momentarily reads as "Completed" instead of
      // "Awaiting review".
      const needsGrade = (form?.fields || []).some(
        (f) => f.scored && (f.type === "short_text" || f.type === "long_text")
      );
      setCompletion(
        payload?.form_completion || {
          responses,
          grading_status: needsGrade ? "pending" : "not_required",
        }
      );
      if (props.setRefreshSection) props.setRefreshSection(true);
    } catch (error) {
      // surfaced by request layer
    } finally {
      setIsSubmitting(false);
    }
  };

  // Read-only fields (+ loading / unavailable / empty states) for the authoring
  // surfaces. The card chrome around this mirrors the complete-mode (employee)
  // card so the header matches what the employee sees.
  const renderReadOnlyFields = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-6">
          <LoadSpinner />
        </div>
      );
    }
    if (!form) {
      return (
        <p className="text-sm text-slate-400">
          This form is currently unavailable.
        </p>
      );
    }
    const flds = form.fields || [];
    if (flds.length === 0) {
      return <p className="text-sm text-slate-400">This form has no fields yet.</p>;
    }
    return flds.map((f) => <ReadOnlyField key={f.id} field={f} />);
  };

  // The section-editor / preview card — same structure as the complete-mode
  // (employee) card so the header (plain icon, name, description, divider) is
  // identical. `showChange` adds the Change action in the section editor.
  const renderEditorReadOnlyCard = (showChange) => (
    <div
      className="rw-form-block my-3 w-full rounded-lg border border-slate-200 bg-white"
      contentEditable={false}
    >
      <div className="px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
            <DescriptionOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold text-slate-900 text-base">
                {form?.name || formName || "Form"}
              </span>
              {showChange && props.onEditAsset && formUuid && (
                <button
                  type="button"
                  onClick={() => props.onEditAsset(formUuid)}
                  className="text-xs font-medium text-violet-700 hover:text-violet-800 shrink-0"
                >
                  Edit
                </button>
              )}
            </div>
            {form?.description && (
              <p className="text-slate-600 text-sm mt-1">{form.description}</p>
            )}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
          {renderReadOnlyFields()}
        </div>
      </div>
    </div>
  );

  // ---------- EDIT MODE (authoring) ----------
  if (isEditMode) {
    if (!formUuid) {
      return (
        <>
          <button
            type="button"
            contentEditable={false}
            onClick={() => setShowPicker(true)}
            className="my-3 w-full p-4 border-2 border-dashed border-violet-300 rounded-xl text-sm text-violet-700 hover:bg-violet-50 hover:border-violet-500 transition-colors flex items-center justify-center gap-2"
          >
            <DescriptionOutlinedIcon style={{ fontSize: 18 }} />
            <span>Select a form</span>
          </button>
          <AddFormToSectionModal
            open={showPicker}
            handleClose={() => setShowPicker(false)}
            handleFormSelect={handleFormSelect}
            homeCompany={homeCompany}
          />
        </>
      );
    }
    // No picker here: a configured embed is edited in place (Edit button →
    // onEditAsset), not swapped, matching the Quiz/SwipeDeck blocks. The picker
    // lives only in the unconfigured branch above (initial selection).
    return renderEditorReadOnlyCard(true);
  }

  // ---------- PREVIEW (track/module editor) ----------
  if (isPreview) {
    return renderEditorReadOnlyCard(false);
  }

  // ---------- COMPLETE MODE (take training) ----------
  if (!formUuid) {
    return (
      <div className="my-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
        Form not configured
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="my-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-6 flex justify-center">
        <LoadSpinner />
      </div>
    );
  }

  // GET failed (or the form was removed) — don't show a fillable empty card.
  if (!form) {
    return (
      <div className="my-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
        This form is currently unavailable.
      </div>
    );
  }

  const fields = form?.fields || [];
  // The completed read-only summary is rendered with the field defs the learner
  // actually submitted against (snapshot), so a later admin edit to the form
  // doesn't blank or mislabel their recorded answers. The fill UI always uses
  // the live fields (a resubmission is made against the current form).
  const completedFields =
    completion?.fields_snapshot && completion.fields_snapshot.length
      ? completion.fields_snapshot
      : fields;

  const renderFieldValue = (f) => {
    const v = responses[f.id];
    if (f.type === "checkbox") return v ? "Yes" : "No";
    if (v === undefined || v === null || v === "") return "—";
    if (f.type === "yes_no") return v === true ? "Yes" : v === false ? "No" : "—";
    return formatAnswer(v, f.type) ?? String(v);
  };

  const isSlides = form?.presentation === "slides";
  // The player finished a submission: mirror handleSubmit's bookkeeping.
  const handlePlayerSubmitted = (completionResult) => {
    if (completionResult?.responses) setResponses(completionResult.responses);
    setCompletion(completionResult);
    // Plain `true` (not a toggle): CompleteTrack refetches when truthy and
    // resets it, so a toggle could flip a pending refresh back off.
    if (props.setRefreshSection) props.setRefreshSection(true);
  };
  const handlePlayerClose = () => {
    setShowPlayer(false);
  };

  // A submitted-summary row: a heading renders as a section divider; every
  // other field as label + the submitted value.
  // Piping for the submitted summary resolves against the SNAPSHOT the learner
  // answered under (a piped source may have been removed/renamed since), not
  // the live form; the fill UI keeps using the live map.
  const completedFieldsById = buildFieldsById(completedFields);
  const pipeCompleted = (text) => resolvePiping(text, responses, completedFieldsById);
  const summaryRow = (f) =>
    // An info screen carries no answer — it's flow, not content to review.
    f.type === "info_screen" ? null : f.type === "heading" ? (
      <h3 key={f.id} className="text-sm font-semibold text-slate-900 !mt-6 pb-2 border-b border-slate-200 first:!mt-0">
        {pipeCompleted(f.label)}
      </h3>
    ) : (
      <div key={f.id}>
        <div className="text-xs font-medium text-slate-500">{pipeCompleted(f.label)}</div>
        <div className="text-sm text-slate-900">{renderFieldValue(f)}</div>
      </div>
    );
  // The submitted summary shows only the fields the learner actually saw
  // (display conditions evaluated against their answers + snapshot).
  const completedVisibleFields = visibleFields(completedFields, responses);

  // Thin wrapper over the shared FormFieldInput (also used by the public form
  // page), wired to this component's `responses` state.
  const renderFieldInput = (f) => (
    <FormFieldInput
      // Checkbox renders its own help as the tick label, so pipe it too.
      field={f.help ? { ...f, help: pipe(f.help) } : f}
      value={responses[f.id]}
      onChange={(val) => setResponse(f.id, val)}
    />
  );

  return (
    <div
      className={`rw-form-block my-3 w-full rounded-lg border ${
        isCompleted
          ? "border-emerald-200 bg-emerald-50"
          : isPendingGrade
          ? "border-amber-200 bg-amber-50"
          : "border-slate-200 bg-white"
      }`}
      contentEditable={false}
    >
      <div className="px-4 py-4">
        <div className="flex items-start gap-3">
          {isCompleted ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
              <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
            </div>
          ) : isPendingGrade ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100">
              <DescriptionOutlinedIcon className="text-amber-600" style={{ fontSize: 20 }} />
            </div>
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
              <DescriptionOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-base">
                {form?.name || formName || "Form"}
              </span>
              {isCompleted && (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 font-medium rounded text-xs">
                  Completed
                </span>
              )}
              {isPendingGrade && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 font-medium rounded text-xs">
                  Submitted · Awaiting review
                </span>
              )}
              {isCompleted && completion?.score !== null && completion?.score !== undefined && (
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-medium rounded text-xs">
                  Score {completion.score}
                  {completion.is_passed === true ? " · Passed" : ""}
                  {completion.is_passed === false ? " · Not passed" : ""}
                </span>
              )}
            </div>
            {form?.description && (
              <p className="text-slate-600 text-sm mt-1">{form.description}</p>
            )}
          </div>
        </div>

        {/* Fields span the full card width (icon only flanks the header). */}
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-4">
              {/* Pending manager grade → read-only summary + awaiting-review
                  note. Distinct from "Completed" (no score yet) and from a
                  failing submission (the learner has not failed). */}
              {isPendingGrade ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-sm font-medium">
                      Submitted, awaiting manager review
                    </p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      Your manager will review and grade your responses.
                    </p>
                  </div>
                  {completedVisibleFields.map(summaryRow)}
                </>
              ) : /* Completed → read-only summary (rendered with the submitted
                  field defs, not the form's current ones) */
              isCompleted ? (
                completedVisibleFields.map(summaryRow)
              ) : canFill && isSlides ? (
                <>
                  {isBlockingFailure && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-amber-800 text-sm font-medium">
                        Not passed
                        {completion?.score !== null &&
                        completion?.score !== undefined
                          ? ` · Score ${completion.score}`
                          : ""}
                      </p>
                      <p className="text-amber-700 text-xs mt-0.5">
                        A passing score is required to complete this section.
                        Start again and resubmit.
                      </p>
                    </div>
                  )}
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-3 sm:flex-col sm:items-start">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {openingScreenTitle || "Ready when you are"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {fields.filter((f) => !isDisplayOnlyType(f.type)).length} questions, one
                        per screen.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPlayer(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 shrink-0"
                    >
                      <PlayArrowIcon style={{ fontSize: 18 }} />
                      {isBlockingFailure ? "Start again" : "Start"}
                    </button>
                  </div>
                </>
              ) : canFill ? (
                <>
                  {isBlockingFailure && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-amber-800 text-sm font-medium">
                        Not passed
                        {completion?.score !== null &&
                        completion?.score !== undefined
                          ? ` · Score ${completion.score}`
                          : ""}
                      </p>
                      <p className="text-amber-700 text-xs mt-0.5">
                        A passing score is required to complete this section.
                        Review your responses and resubmit.
                      </p>
                    </div>
                  )}
                  {shownFields.map((f) =>
                    f.type === "info_screen" ? (
                      <FormInfoScreen
                        key={f.id}
                        label={pipe(f.label)}
                        help={pipe(f.help)}
                        imageUrl={f.image_url}
                        className="!mt-8 first:!mt-0"
                      />
                    ) : f.type === "heading" ? (
                      <div key={f.id} className="!mt-10 first:!mt-0">
                        <h3 className="text-base font-semibold text-slate-900 pb-2.5 border-b border-slate-200">
                          {pipe(f.label)}
                        </h3>
                        {f.help && (
                          <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap rw-form-help">
                            {pipe(f.help)}
                          </p>
                        )}
                        <FormFieldImage url={f.image_url} size="inline" className="mt-3" />
                      </div>
                    ) : (
                      <div key={f.id}>
                        <label className="block text-sm font-medium text-slate-800 mb-1.5">
                          {pipe(f.label)}
                          {f.required && <span className="text-red-500"> *</span>}
                        </label>
                        {f.help && f.type !== "checkbox" && (
                          <p className="text-xs text-slate-500 whitespace-pre-wrap rw-form-help">
                            {pipe(f.help)}
                          </p>
                        )}
                        <FormFieldImage url={f.image_url} size="inline" className="my-2" />
                        {renderFieldInput(f)}
                      </div>
                    )
                  )}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || requiredUnfilled}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isSubmitting || requiredUnfilled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-violet-700 hover:bg-violet-800 text-white"
                    }`}
                  >
                    {isSubmitting && <LoadSpinner />}
                    {isBlockingFailure ? "Resubmit" : "Submit"}
                  </button>
                </>
              ) : form?.completed_by_role === "manager" ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    Waiting for manager input. Your manager must complete this
                    before you can continue.
                  </p>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-slate-600 text-sm">
                    This form is completed by the learner and hasn't been
                    submitted yet.
                  </p>
                </div>
              )}
              {canManagerGrade && (
                <button
                  type="button"
                  onClick={openGrade}
                  disabled={gradeLoading}
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {gradeLoading
                    ? "Opening…"
                    : completion.grading_status === "pending"
                    ? "Grade submission"
                    : "Edit grade"}
                </button>
              )}
        </div>
      </div>
      {gradeData && (
        <FormGradeModal
          open={!!gradeData}
          record={gradeData.record}
          submission={gradeData.submission}
          homeCompany={homeCompany}
          onClose={() => setGradeData(null)}
          onGraded={handleGradedInline}
        />
      )}
      {/* Slide-mode player, portaled to body: this block can live inside a
          transformed ancestor (a MUI modal Box), which would otherwise become
          the containing block for the player's fixed positioning. Same reason
          SwipeDeck.js portals its player. */}
      {showPlayer &&
        ReactDOM.createPortal(
          <React.Suspense fallback={null}>
            <FormPlayerLazy
              mode="tracked"
              form={form}
              companyId={homeCompany}
              formUuid={formUuid}
              sectionUuid={sectionUUID}
              employeeID={employeeID}
              initialResponses={isBlockingFailure ? responses : undefined}
              onSubmitted={handlePlayerSubmitted}
              onClose={handlePlayerClose}
            />
          </React.Suspense>,
          document.body
        )}
    </div>
  );
}

export default Form;
